import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';

function SubscriptionManagement() {
  const [activeTab, setActiveTab] = useState('all'); // 'all' or 'requests'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // All subscriptions state
  const [restaurants, setRestaurants] = useState([]);
  const [planFilter, setPlanFilter] = useState('');

  // Plan change requests state
  const [changeRequests, setChangeRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');

  // Modal states
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [newPlanType, setNewPlanType] = useState('');
  const [changeReason, setChangeReason] = useState('');

  // Process request modal states
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestAction, setRequestAction] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  // Subscription deduction history + revert modal
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [revertingId, setRevertingId] = useState(null);
  const [revertReasons, setRevertReasons] = useState({});

  // Auto-revert automation toggle (<₹10k monthly sales → subscription reverted)
  const [autoRevert, setAutoRevert] = useState({ enabled: true, salesThreshold: 10000, loaded: false });
  const [autoRevertSaving, setAutoRevertSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await adminAPI.getSubscriptionAutoRevertSettings();
        const d = res.data?.data || {};
        setAutoRevert({ enabled: d.enabled !== false, salesThreshold: d.salesThreshold ?? 10000, loaded: true });
      } catch (e) {
        setAutoRevert((p) => ({ ...p, loaded: true }));
      }
    })();
  }, []);

  const toggleAutoRevert = async (nextEnabled) => {
    setAutoRevertSaving(true);
    try {
      const res = await adminAPI.updateSubscriptionAutoRevertSettings({ enabled: nextEnabled });
      const d = res.data?.data || {};
      setAutoRevert({ enabled: d.enabled !== false, salesThreshold: d.salesThreshold ?? 10000, loaded: true });
      setSuccess(`Subscription auto-revert ${nextEnabled ? 'ENABLED' : 'DISABLED'}.`);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to update auto-revert setting');
    } finally {
      setAutoRevertSaving(false);
    }
  };

  // Auto-DEDUCT automation toggle (the automatic monthly subscription fee charge)
  const [autoDeduct, setAutoDeduct] = useState({ enabled: true, loaded: false });
  const [autoDeductSaving, setAutoDeductSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await adminAPI.getSubscriptionAutoDeductSettings();
        const d = res.data?.data || {};
        setAutoDeduct({ enabled: d.enabled !== false, loaded: true });
      } catch (e) {
        setAutoDeduct((p) => ({ ...p, loaded: true }));
      }
    })();
  }, []);

  const toggleAutoDeduct = async (nextEnabled) => {
    setAutoDeductSaving(true);
    try {
      const res = await adminAPI.updateSubscriptionAutoDeductSettings({ enabled: nextEnabled });
      const d = res.data?.data || {};
      setAutoDeduct({ enabled: d.enabled !== false, loaded: true });
      setSuccess(`Auto subscription deduction ${nextEnabled ? 'ENABLED' : 'DISABLED'}.`);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to update auto-deduct setting');
    } finally {
      setAutoDeductSaving(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'all') {
      fetchAllSubscriptions();
    } else {
      fetchPlanChangeRequests();
    }
  }, [activeTab, planFilter, statusFilter]);

  const fetchAllSubscriptions = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (planFilter) params.planType = planFilter;

      const response = await adminAPI.getAllRestaurantSubscriptions(params);
      setRestaurants(response.data.data.restaurants);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      setError('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlanChangeRequests = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;

      const response = await adminAPI.getPlanChangeRequests(params);
      setChangeRequests(response.data.data.requests);
    } catch (error) {
      console.error('Error fetching change requests:', error);
      setError('Failed to load change requests');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePlan = async () => {
    if (!newPlanType) {
      setError('Please select a plan type');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await adminAPI.changeRestaurantPlan(selectedRestaurant._id, newPlanType, changeReason);
      setSuccess(`Successfully changed plan for ${selectedRestaurant.name}`);
      setShowChangePlanModal(false);
      setSelectedRestaurant(null);
      setNewPlanType('');
      setChangeReason('');
      fetchAllSubscriptions();
    } catch (error) {
      console.error('Error changing plan:', error);
      setError(error.response?.data?.message || 'Failed to change plan');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessRequest = async () => {
    if (!requestAction) {
      setError('Please select an action');
      return;
    }

    if (requestAction === 'reject' && !rejectionReason) {
      setError('Please provide a rejection reason');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await adminAPI.processPlanChangeRequest(
        selectedRequest.requestId,
        requestAction,
        rejectionReason
      );
      setSuccess(`Request ${requestAction}ed successfully`);
      setShowProcessModal(false);
      setSelectedRequest(null);
      setRequestAction('');
      setRejectionReason('');
      fetchPlanChangeRequests();
    } catch (error) {
      console.error('Error processing request:', error);
      setError(error.response?.data?.message || 'Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  const openHistory = async (restaurant) => {
    setSelectedRestaurant(restaurant);
    setHistoryData(null);
    setRevertReasons({});
    setShowHistoryModal(true);
    setHistoryLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await adminAPI.getRestaurantSubscriptionDeductions(restaurant._id);
      setHistoryData(res.data.data);
    } catch (error) {
      console.error('Error loading subscription history:', error);
      setError(error.response?.data?.message || 'Failed to load subscription history');
      setShowHistoryModal(false);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleRevert = async (payout) => {
    const reason = (revertReasons[payout.id] || '').trim();
    if (!reason) {
      setError('Please enter a reason before reverting.');
      return;
    }
    if (!window.confirm(`Revert this month's subscription deduction of ₹${payout.totalDeducted}? The amount will be credited back to the restaurant's balance.`)) {
      return;
    }
    setRevertingId(payout.id);
    setError('');
    setSuccess('');
    try {
      const res = await adminAPI.revertSubscriptionDeduction(selectedRestaurant._id, payout.payoutId, reason);
      setSuccess(res.data.message || 'Subscription deduction reverted.');
      const refreshed = await adminAPI.getRestaurantSubscriptionDeductions(selectedRestaurant._id);
      setHistoryData(refreshed.data.data);
    } catch (error) {
      console.error('Error reverting deduction:', error);
      setError(error.response?.data?.message || 'Failed to revert deduction');
    } finally {
      setRevertingId(null);
    }
  };

  const getPlanBadgeColor = (planType) => {
    switch (planType) {
      case 'one_time':
        return 'var(--primary)';
      case 'commission':
        return 'var(--info)';
      default:
        return 'var(--gray-500)';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'pending':
        return 'var(--warning)';
      case 'approved':
        return 'var(--success)';
      case 'rejected':
        return 'var(--danger)';
      default:
        return 'var(--gray-500)';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPlanType = (type) => {
    if (!type || type === 'none') return 'No Plan';
    return type === 'one_time' ? 'One-Time (₹50,000)' : 'Commission (10%)';
  };

  const getPaymentStatus = (restaurant) => {
    const plan = restaurant.subscriptionPlan;

    // Only show payment status for one-time plans
    if (!plan || plan.type !== 'one_time') {
      return { text: 'N/A', color: 'var(--gray-500)', amount: '' };
    }

    const initialPaid = plan.oneTimeFee?.initialPaymentStatus === 'paid';
    const remainingPaid = plan.oneTimeFee?.remainingPaymentStatus === 'paid';

    if (initialPaid && remainingPaid) {
      return {
        text: 'Fully Paid',
        color: 'var(--success)',
        amount: '₹50,000'
      };
    } else if (initialPaid && !remainingPaid) {
      return {
        text: 'Initial Paid',
        color: 'var(--warning)',
        amount: '₹10,000'
      };
    } else {
      return {
        text: 'No Payment',
        color: 'var(--danger)',
        amount: '₹0'
      };
    }
  };

  return (
    <div style={{ padding: 'var(--space-xl)' }}>
      {/* Header */}
      <div style={{ marginBottom: 'var(--space-2xl)' }}>
        <h1 style={{ marginBottom: 'var(--space-sm)' }}>💰 Subscription Management</h1>
        <p style={{ color: 'var(--gray-600)' }}>Manage restaurant subscription plans and requests</p>
      </div>

      {/* Auto-revert automation toggle */}
      <div style={{
        marginBottom: 'var(--space-lg)', padding: 'var(--space-lg)', borderRadius: 12,
        border: '1px solid var(--gray-200)', background: autoRevert.enabled ? 'rgba(79,205,196,0.08)' : 'var(--gray-50)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap'
      }}>
        <div>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>
            🔁 Auto-revert subscription for low-volume restaurants
          </div>
          <div style={{ fontSize: 13, color: 'var(--gray-600)' }}>
            When ON, a daily job automatically reverts a restaurant's monthly subscription charge (credits it back)
            if its sales that month were below <b>₹{Number(autoRevert.salesThreshold).toLocaleString('en-IN')}</b>.
            Turn OFF to revert manually below.
          </div>
        </div>
        <button
          onClick={() => toggleAutoRevert(!autoRevert.enabled)}
          disabled={autoRevertSaving || !autoRevert.loaded}
          style={{
            minWidth: 130, padding: '10px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
            fontWeight: 700, color: 'white',
            background: autoRevert.enabled ? 'var(--success, #059669)' : 'var(--gray-400, #9CA3AF)'
          }}
        >
          {autoRevertSaving ? 'Saving…' : autoRevert.enabled ? '● Automation ON' : '○ Automation OFF'}
        </button>
      </div>

      {/* Auto-DEDUCT automation toggle */}
      <div style={{
        marginBottom: 'var(--space-lg)', padding: 'var(--space-lg)', borderRadius: 12,
        border: '1px solid var(--gray-200)', background: autoDeduct.enabled ? 'rgba(79,205,196,0.08)' : 'var(--gray-50)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap'
      }}>
        <div>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>
            💳 Auto-deduct monthly subscription fee
          </div>
          <div style={{ fontSize: 13, color: 'var(--gray-600)' }}>
            When ON, the monthly subscription fee is charged automatically (auto-renewal) from restaurant payouts.
            Turn <b>OFF</b> to stop all automatic deductions platform-wide (both the on-access charge and the daily cron).
          </div>
        </div>
        <button
          onClick={() => toggleAutoDeduct(!autoDeduct.enabled)}
          disabled={autoDeductSaving || !autoDeduct.loaded}
          style={{
            minWidth: 130, padding: '10px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
            fontWeight: 700, color: 'white',
            background: autoDeduct.enabled ? 'var(--success, #059669)' : 'var(--gray-400, #9CA3AF)'
          }}
        >
          {autoDeductSaving ? 'Saving…' : autoDeduct.enabled ? '● Auto-deduct ON' : '○ Auto-deduct OFF'}
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="error" style={{ marginBottom: 'var(--space-lg)' }}>
          {error}
        </div>
      )}
      {success && (
        <div className="success" style={{ marginBottom: 'var(--space-lg)', background: 'rgba(79, 205, 196, 0.1)', border: '2px solid var(--success)', color: 'var(--success)' }}>
          {success}
        </div>
      )}

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: 'var(--space-md)',
        marginBottom: 'var(--space-xl)',
        borderBottom: '2px solid var(--gray-200)'
      }}>
        <button
          onClick={() => setActiveTab('all')}
          style={{
            padding: 'var(--space-md) var(--space-xl)',
            background: activeTab === 'all' ? 'var(--primary)' : 'transparent',
            color: activeTab === 'all' ? 'white' : 'var(--gray-700)',
            border: 'none',
            borderBottom: activeTab === 'all' ? '3px solid var(--primary)' : 'none',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all var(--transition-base)'
          }}
        >
          📊 All Subscriptions
        </button>
        <button
          onClick={() => setActiveTab('requests')}
          style={{
            padding: 'var(--space-md) var(--space-xl)',
            background: activeTab === 'requests' ? 'var(--primary)' : 'transparent',
            color: activeTab === 'requests' ? 'white' : 'var(--gray-700)',
            border: 'none',
            borderBottom: activeTab === 'requests' ? '3px solid var(--primary)' : 'none',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all var(--transition-base)',
            position: 'relative'
          }}
        >
          📋 Plan Change Requests
          {changeRequests.filter(r => r.status === 'pending').length > 0 && (
            <span style={{
              position: 'absolute',
              top: '5px',
              right: '5px',
              background: 'var(--danger)',
              color: 'white',
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              fontSize: 'var(--font-size-xs)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {changeRequests.filter(r => r.status === 'pending').length}
            </span>
          )}
        </button>
      </div>

      {/* All Subscriptions Tab */}
      {activeTab === 'all' && (
        <div>
          {/* Filters */}
          <div style={{
            display: 'flex',
            gap: 'var(--space-md)',
            marginBottom: 'var(--space-xl)',
            alignItems: 'center'
          }}>
            <label style={{ fontWeight: '600' }}>Filter by Plan:</label>
            <select
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value)}
              style={{
                padding: 'var(--space-sm) var(--space-md)',
                borderRadius: 'var(--radius-md)',
                border: '2px solid var(--gray-300)'
              }}
            >
              <option value="">All Plans</option>
              <option value="one_time">One-Time Plan</option>
              <option value="commission">Commission Plan</option>
              <option value="none">No Plan</option>
            </select>
            <button
              onClick={fetchAllSubscriptions}
              className="btn btn-secondary"
              style={{ marginLeft: 'auto' }}
            >
              🔄 Refresh
            </button>
          </div>

          {/* Restaurants Table */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
              Loading...
            </div>
          ) : restaurants.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
              <p>No restaurants found</p>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--gray-100)', borderBottom: '2px solid var(--gray-300)' }}>
                    <th style={{ padding: 'var(--space-md)', textAlign: 'left' }}>Restaurant</th>
                    <th style={{ padding: 'var(--space-md)', textAlign: 'center' }}>Current Plan</th>
                    <th style={{ padding: 'var(--space-md)', textAlign: 'center' }}>Payment Status</th>
                    <th style={{ padding: 'var(--space-md)', textAlign: 'center' }}>Selected On</th>
                    <th style={{ padding: 'var(--space-md)', textAlign: 'center' }}>Status</th>
                    <th style={{ padding: 'var(--space-md)', textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {restaurants.map((restaurant) => (
                    <tr key={restaurant._id} style={{ borderBottom: '1px solid var(--gray-200)' }}>
                      <td style={{ padding: 'var(--space-md)' }}>
                        <div>
                          <div style={{ fontWeight: '600' }}>{restaurant.name}</div>
                          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)' }}>
                            {restaurant.ownerId?.name} • {restaurant.ownerId?.email}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: 'var(--space-md)', textAlign: 'center' }}>
                        <span style={{
                          padding: 'var(--space-xs) var(--space-md)',
                          borderRadius: 'var(--radius-full)',
                          background: `${getPlanBadgeColor(restaurant.subscriptionPlan?.type)}20`,
                          color: getPlanBadgeColor(restaurant.subscriptionPlan?.type),
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: '600'
                        }}>
                          {formatPlanType(restaurant.subscriptionPlan?.type)}
                        </span>
                      </td>
                      <td style={{ padding: 'var(--space-md)', textAlign: 'center' }}>
                        {(() => {
                          const paymentStatus = getPaymentStatus(restaurant);
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-xs)' }}>
                              <span style={{
                                padding: 'var(--space-xs) var(--space-md)',
                                borderRadius: 'var(--radius-full)',
                                background: `${paymentStatus.color}20`,
                                color: paymentStatus.color,
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: '600'
                              }}>
                                {paymentStatus.text}
                              </span>
                              {paymentStatus.amount && (
                                <span style={{
                                  fontSize: 'var(--font-size-xs)',
                                  color: 'var(--gray-600)',
                                  fontWeight: '600'
                                }}>
                                  {paymentStatus.amount}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td style={{ padding: 'var(--space-md)', textAlign: 'center' }}>
                        {formatDate(restaurant.subscriptionPlan?.selectedAt)}
                      </td>
                      <td style={{ padding: 'var(--space-md)', textAlign: 'center' }}>
                        <span style={{
                          padding: 'var(--space-xs) var(--space-md)',
                          borderRadius: 'var(--radius-full)',
                          background: restaurant.isApproved ? 'rgba(79, 205, 196, 0.1)' : 'rgba(255, 184, 77, 0.1)',
                          color: restaurant.isApproved ? 'var(--success)' : 'var(--warning)',
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: '600'
                        }}>
                          {restaurant.isApproved ? 'Approved' : 'Pending'}
                        </span>
                      </td>
                      <td style={{ padding: 'var(--space-md)', textAlign: 'center' }}>
                        <button
                          onClick={() => {
                            setSelectedRestaurant(restaurant);
                            setNewPlanType('');
                            setChangeReason('');
                            setShowChangePlanModal(true);
                          }}
                          className="btn btn-primary"
                          style={{
                            padding: 'var(--space-xs) var(--space-md)',
                            fontSize: 'var(--font-size-sm)'
                          }}
                        >
                          Change Plan
                        </button>
                        <button
                          onClick={() => openHistory(restaurant)}
                          className="btn btn-secondary"
                          style={{
                            padding: 'var(--space-xs) var(--space-md)',
                            fontSize: 'var(--font-size-sm)',
                            marginLeft: 'var(--space-xs)'
                          }}
                        >
                          Deductions
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Plan Change Requests Tab */}
      {activeTab === 'requests' && (
        <div>
          {/* Filters */}
          <div style={{
            display: 'flex',
            gap: 'var(--space-md)',
            marginBottom: 'var(--space-xl)',
            alignItems: 'center'
          }}>
            <label style={{ fontWeight: '600' }}>Filter by Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: 'var(--space-sm) var(--space-md)',
                borderRadius: 'var(--radius-md)',
                border: '2px solid var(--gray-300)'
              }}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            <button
              onClick={fetchPlanChangeRequests}
              className="btn btn-secondary"
              style={{ marginLeft: 'auto' }}
            >
              🔄 Refresh
            </button>
          </div>

          {/* Requests Table */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
              Loading...
            </div>
          ) : changeRequests.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
              <p>No plan change requests found</p>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--gray-100)', borderBottom: '2px solid var(--gray-300)' }}>
                    <th style={{ padding: 'var(--space-md)', textAlign: 'left' }}>Restaurant</th>
                    <th style={{ padding: 'var(--space-md)', textAlign: 'center' }}>From Plan</th>
                    <th style={{ padding: 'var(--space-md)', textAlign: 'center' }}>To Plan</th>
                    <th style={{ padding: 'var(--space-md)', textAlign: 'left' }}>Reason</th>
                    <th style={{ padding: 'var(--space-md)', textAlign: 'center' }}>Requested</th>
                    <th style={{ padding: 'var(--space-md)', textAlign: 'center' }}>Status</th>
                    <th style={{ padding: 'var(--space-md)', textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {changeRequests.map((request) => (
                    <tr key={request.requestId} style={{ borderBottom: '1px solid var(--gray-200)' }}>
                      <td style={{ padding: 'var(--space-md)' }}>
                        <div>
                          <div style={{ fontWeight: '600' }}>{request.restaurantName}</div>
                          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)' }}>
                            {request.ownerInfo?.name}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: 'var(--space-md)', textAlign: 'center' }}>
                        <span style={{
                          padding: 'var(--space-xs) var(--space-md)',
                          borderRadius: 'var(--radius-full)',
                          background: `${getPlanBadgeColor(request.fromPlan)}20`,
                          color: getPlanBadgeColor(request.fromPlan),
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: '600'
                        }}>
                          {formatPlanType(request.fromPlan)}
                        </span>
                      </td>
                      <td style={{ padding: 'var(--space-md)', textAlign: 'center' }}>
                        <span style={{
                          padding: 'var(--space-xs) var(--space-md)',
                          borderRadius: 'var(--radius-full)',
                          background: `${getPlanBadgeColor(request.toPlan)}20`,
                          color: getPlanBadgeColor(request.toPlan),
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: '600'
                        }}>
                          {formatPlanType(request.toPlan)}
                        </span>
                      </td>
                      <td style={{ padding: 'var(--space-md)' }}>
                        <div style={{ maxWidth: '200px', fontSize: 'var(--font-size-sm)' }}>
                          {request.reason || 'No reason provided'}
                        </div>
                      </td>
                      <td style={{ padding: 'var(--space-md)', textAlign: 'center', fontSize: 'var(--font-size-sm)' }}>
                        {formatDate(request.requestedAt)}
                      </td>
                      <td style={{ padding: 'var(--space-md)', textAlign: 'center' }}>
                        <span style={{
                          padding: 'var(--space-xs) var(--space-md)',
                          borderRadius: 'var(--radius-full)',
                          background: `${getStatusBadgeColor(request.status)}20`,
                          color: getStatusBadgeColor(request.status),
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: '600',
                          textTransform: 'capitalize'
                        }}>
                          {request.status}
                        </span>
                      </td>
                      <td style={{ padding: 'var(--space-md)', textAlign: 'center' }}>
                        {request.status === 'pending' ? (
                          <button
                            onClick={() => {
                              setSelectedRequest(request);
                              setRequestAction('');
                              setRejectionReason('');
                              setShowProcessModal(true);
                            }}
                            className="btn btn-primary"
                            style={{
                              padding: 'var(--space-xs) var(--space-md)',
                              fontSize: 'var(--font-size-sm)'
                            }}
                          >
                            Process
                          </button>
                        ) : (
                          <span style={{ color: 'var(--gray-500)', fontSize: 'var(--font-size-sm)' }}>
                            Processed
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Change Plan Modal */}
      {showChangePlanModal && selectedRestaurant && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ maxWidth: '500px', width: '90%', maxHeight: '90vh', overflow: 'auto' }}>
            <h2 style={{ marginBottom: 'var(--space-lg)' }}>Change Subscription Plan</h2>

            <div style={{ marginBottom: 'var(--space-lg)' }}>
              <strong>Restaurant:</strong> {selectedRestaurant.name}
            </div>

            <div style={{ marginBottom: 'var(--space-lg)' }}>
              <strong>Current Plan:</strong>{' '}
              <span style={{
                padding: 'var(--space-xs) var(--space-md)',
                borderRadius: 'var(--radius-full)',
                background: `${getPlanBadgeColor(selectedRestaurant.subscriptionPlan?.type)}20`,
                color: getPlanBadgeColor(selectedRestaurant.subscriptionPlan?.type),
                fontWeight: '600'
              }}>
                {formatPlanType(selectedRestaurant.subscriptionPlan?.type)}
              </span>
            </div>

            <div className="form-group">
              <label>New Plan *</label>
              <select
                value={newPlanType}
                onChange={(e) => setNewPlanType(e.target.value)}
              >
                <option value="">Select Plan</option>
                <option value="one_time">One-Time (₹50,000)</option>
                <option value="commission">Commission (10%)</option>
              </select>
            </div>

            <div className="form-group">
              <label>Reason (Optional)</label>
              <textarea
                rows="3"
                value={changeReason}
                onChange={(e) => setChangeReason(e.target.value)}
                placeholder="Reason for changing the plan..."
              />
            </div>

            <div style={{ display: 'flex', gap: 'var(--space-md)', marginTop: 'var(--space-xl)' }}>
              <button
                onClick={() => {
                  setShowChangePlanModal(false);
                  setSelectedRestaurant(null);
                  setNewPlanType('');
                  setChangeReason('');
                }}
                className="btn"
                style={{ flex: 1 }}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleChangePlan}
                className="btn btn-primary"
                style={{ flex: 1 }}
                disabled={loading || !newPlanType}
              >
                {loading ? 'Changing...' : 'Change Plan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Process Request Modal */}
      {showProcessModal && selectedRequest && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ maxWidth: '500px', width: '90%', maxHeight: '90vh', overflow: 'auto' }}>
            <h2 style={{ marginBottom: 'var(--space-lg)' }}>Process Plan Change Request</h2>

            <div style={{ marginBottom: 'var(--space-lg)' }}>
              <strong>Restaurant:</strong> {selectedRequest.restaurantName}
            </div>

            <div style={{ marginBottom: 'var(--space-md)' }}>
              <strong>From:</strong>{' '}
              <span style={{
                padding: 'var(--space-xs) var(--space-md)',
                borderRadius: 'var(--radius-full)',
                background: `${getPlanBadgeColor(selectedRequest.fromPlan)}20`,
                color: getPlanBadgeColor(selectedRequest.fromPlan),
                fontWeight: '600'
              }}>
                {formatPlanType(selectedRequest.fromPlan)}
              </span>
            </div>

            <div style={{ marginBottom: 'var(--space-lg)' }}>
              <strong>To:</strong>{' '}
              <span style={{
                padding: 'var(--space-xs) var(--space-md)',
                borderRadius: 'var(--radius-full)',
                background: `${getPlanBadgeColor(selectedRequest.toPlan)}20`,
                color: getPlanBadgeColor(selectedRequest.toPlan),
                fontWeight: '600'
              }}>
                {formatPlanType(selectedRequest.toPlan)}
              </span>
            </div>

            <div style={{ marginBottom: 'var(--space-lg)' }}>
              <strong>Reason:</strong>
              <div style={{
                marginTop: 'var(--space-sm)',
                padding: 'var(--space-md)',
                background: 'var(--gray-100)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)'
              }}>
                {selectedRequest.reason || 'No reason provided'}
              </div>
            </div>

            <div className="form-group">
              <label>Action *</label>
              <select
                value={requestAction}
                onChange={(e) => setRequestAction(e.target.value)}
              >
                <option value="">Select Action</option>
                <option value="approve">Approve Request</option>
                <option value="reject">Reject Request</option>
              </select>
            </div>

            {requestAction === 'reject' && (
              <div className="form-group">
                <label>Rejection Reason *</label>
                <textarea
                  rows="3"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Provide a reason for rejection..."
                />
              </div>
            )}

            <div style={{ display: 'flex', gap: 'var(--space-md)', marginTop: 'var(--space-xl)' }}>
              <button
                onClick={() => {
                  setShowProcessModal(false);
                  setSelectedRequest(null);
                  setRequestAction('');
                  setRejectionReason('');
                }}
                className="btn"
                style={{ flex: 1 }}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleProcessRequest}
                className="btn btn-primary"
                style={{ flex: 1 }}
                disabled={loading || !requestAction || (requestAction === 'reject' && !rejectionReason)}
              >
                {loading ? 'Processing...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscription deduction history + revert */}
      {showHistoryModal && selectedRestaurant && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="card" style={{ maxWidth: '780px', width: '94%', maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-lg)' }}>
              <h2 style={{ margin: 0 }}>Subscription — {selectedRestaurant.name}</h2>
              <button className="btn btn-secondary" onClick={() => setShowHistoryModal(false)} style={{ padding: 'var(--space-xs) var(--space-md)' }}>Close</button>
            </div>

            {historyLoading ? (
              <div style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>Loading…</div>
            ) : !historyData ? (
              <div style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>No data</div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: 'var(--space-xl)', flexWrap: 'wrap', marginBottom: 'var(--space-md)' }}>
                  <div><strong>Onboarded:</strong> {formatDate(historyData.onboardedAt)}</div>
                  <div><strong>Plan:</strong> {historyData.subscription?.type || 'none'}</div>
                  <div><strong>Status:</strong> {historyData.subscription?.status || '—'}</div>
                  <div><strong>Monthly fee:</strong> ₹{historyData.subscription?.monthlyFee ?? 0}</div>
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-xl)', flexWrap: 'wrap', marginBottom: 'var(--space-lg)', fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)' }}>
                  <div>Total deducted: <strong>₹{historyData.totals?.totalDeducted ?? 0}</strong></div>
                  <div>Total reverted: <strong>₹{historyData.totals?.totalReversed ?? 0}</strong></div>
                  <div>Deductions: <strong>{historyData.totals?.deductionCount ?? 0}</strong></div>
                </div>

                {(!historyData.history || historyData.history.length === 0) ? (
                  <div style={{ textAlign: 'center', padding: 'var(--space-xl)', color: 'var(--gray-600)' }}>No subscription deductions yet.</div>
                ) : (
                  <div style={{ overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid var(--gray-200)', textAlign: 'left' }}>
                          <th style={{ padding: 'var(--space-sm)' }}>Period</th>
                          <th style={{ padding: 'var(--space-sm)' }}>Charged</th>
                          <th style={{ padding: 'var(--space-sm)', textAlign: 'right' }}>Base</th>
                          <th style={{ padding: 'var(--space-sm)', textAlign: 'right' }}>GST</th>
                          <th style={{ padding: 'var(--space-sm)', textAlign: 'right' }}>Total</th>
                          <th style={{ padding: 'var(--space-sm)', textAlign: 'center' }}>Status</th>
                          <th style={{ padding: 'var(--space-sm)' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historyData.history.map((d) => (
                          <tr key={d.id} style={{ borderBottom: '1px solid var(--gray-200)' }}>
                            <td style={{ padding: 'var(--space-sm)' }}>{formatDate(d.periodStart)} – {formatDate(d.periodEnd)}</td>
                            <td style={{ padding: 'var(--space-sm)' }}>{formatDate(d.chargedAt)}</td>
                            <td style={{ padding: 'var(--space-sm)', textAlign: 'right' }}>₹{d.baseFee}</td>
                            <td style={{ padding: 'var(--space-sm)', textAlign: 'right' }}>₹{d.gstAmount}</td>
                            <td style={{ padding: 'var(--space-sm)', textAlign: 'right', fontWeight: 600 }}>₹{d.totalDeducted}</td>
                            <td style={{ padding: 'var(--space-sm)', textAlign: 'center' }}>
                              <span style={{ padding: '2px 8px', borderRadius: 'var(--radius-full)', fontSize: 'var(--font-size-xs)', fontWeight: 600, background: d.reverted ? 'rgba(255,184,77,0.15)' : 'rgba(79,205,196,0.15)', color: d.reverted ? 'var(--warning)' : 'var(--success)' }}>
                                {d.reverted ? 'Reverted' : d.status}
                              </span>
                            </td>
                            <td style={{ padding: 'var(--space-sm)' }}>
                              {d.canRevert ? (
                                <div style={{ display: 'flex', gap: 'var(--space-xs)', alignItems: 'center' }}>
                                  <input
                                    type="text"
                                    placeholder="Reason"
                                    value={revertReasons[d.id] || ''}
                                    onChange={(e) => setRevertReasons({ ...revertReasons, [d.id]: e.target.value })}
                                    style={{ padding: '4px 8px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-xs)', width: '120px' }}
                                  />
                                  <button
                                    onClick={() => handleRevert(d)}
                                    disabled={revertingId === d.id}
                                    style={{ padding: '4px 10px', fontSize: 'var(--font-size-xs)', background: 'var(--danger)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontWeight: 600 }}
                                  >
                                    {revertingId === d.id ? '…' : 'Revert'}
                                  </button>
                                </div>
                              ) : (
                                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)' }}>
                                  {d.reverted ? (d.reversalReason || 'Reverted') : '—'}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SubscriptionManagement;
